import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MaterialLink from './material-link';
import MaterialLinkButton from './material-link-button';
import { DISCORD_ENABLED } from '../constants/DISCORD_ENABLED';

const navSections = [
  {
    heading: 'Explore',
    links: [
      { label: 'Calendar', href: '/calendar', internal: true },
      { label: 'Cities', href: '/cities', internal: true },
      { label: 'About', href: '/about', internal: true },
    ],
  },
  {
    heading: 'Community',
    links: [
      { label: 'Groups', href: '/groups', internal: true },
      { label: 'Add Event', href: '/events/new', internal: true },
      { label: 'Features', href: '/features', internal: true },
    ],
  },
  {
    heading: 'Help',
    links: [
      { label: 'Group Ride Guide', href: '/group-ride-guide', internal: true },
      { label: 'Event Listing Guide', href: '/event-listing-guide', internal: true },
      { label: 'Notifications Guide', href: '/notifications-guide', internal: true },
      { label: 'Calendar Guide', href: '/calendar-guide', internal: true },
      ...(DISCORD_ENABLED ? [{ label: 'Discord', href: '/discord', internal: true }] : []),
      { label: 'Contact', href: '/contact', internal: true },
    ],
  },
];

const socialLinks = [
  { href: 'https://www.instagram.com/everydayridescom', title: 'Instagram', icon: '/static/instagram-brands.svg' },
  { href: 'https://facebook.com/everydayridescom', title: 'Facebook', icon: '/static/facebook-square-brands.svg' },
];

const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        mt: 6,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Support section */}
      <Box sx={{ textAlign: 'center', py: 2, px: 2, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Everyday Rides is a free service built for the community. Please consider supporting the site.
        </Typography>
        <MaterialLinkButton
          href="/support?ref=footer"
          variant="outlined"
          size="small"
        >
          Support Everyday Rides
        </MaterialLinkButton>
      </Box>

      <Divider />

      {/* Nav links */}
      <Box sx={{ px: { xs: 3, sm: 4 }, py: 2, maxWidth: 900, mx: 'auto' }}>
        <Grid container spacing={3}>
          {navSections.map((section) => (
            <Grid key={section.heading} size={{ xs: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {section.heading}
              </Typography>
              <Stack spacing={0.5}>
                {section.links.map((link) =>
                  link.internal ? (
                    <MaterialLink
                      key={link.href}
                      href={link.href}
                      underline="hover"
                      color="text.secondary"
                      variant="body2"
                    >
                      {link.label}
                    </MaterialLink>
                  ) : (
                    <MLink
                      key={link.href}
                      href={link.href}
                      underline="hover"
                      color="text.secondary"
                      variant="body2"
                    >
                      {link.label}
                    </MLink>
                  )
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider />

      {/* Bottom bar: social icons + legal links */}
      <Stack alignItems="center" spacing={0.5} sx={{ py: 1, maxWidth: 900, mx: 'auto' }}>
        <Stack direction="row" spacing={0.5}>
          {socialLinks.map((social) => (
            <IconButton
              key={social.title}
              component="a"
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              title={`Everyday Rides on ${social.title}`}
              size="small"
              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img width="22" height="22" src={social.icon} alt={social.title} />
            </IconButton>
          ))}
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <MaterialLink href="/terms" underline="hover" color="text.secondary" variant="caption">
            Terms
          </MaterialLink>
          <Typography variant="caption" color="text.secondary">·</Typography>
          <MaterialLink href="/privacy" underline="hover" color="text.secondary" variant="caption">
            Privacy
          </MaterialLink>
          <Typography variant="caption" color="text.secondary">·</Typography>
          <MaterialLink href="/contact" underline="hover" color="text.secondary" variant="caption">
            Contact
          </MaterialLink>
        </Stack>
      </Stack>

      {/* Disclaimer + copyright */}
      <Box sx={{ textAlign: 'center', px: 3, py: 1.5, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="caption" color="text.secondary" component="p">
          Everyday Rides is a free, community-powered calendar. Events are posted by the community and are not sponsored or endorsed by Everyday Rides.
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }} suppressHydrationWarning>
          &copy; {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
